import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner, Card, Tab, Tabs } from 'react-bootstrap';
import { FaMobile, FaTv, FaSearch, FaInfoCircle, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import api from '../services/api';

const HLRModal = ({ show, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('hlr');
  
  // Form states
  const [hlrForm, setHlrForm] = useState({
    mobile: '',
    operator: 'Airtel'
  });
  
  const [dthForm, setDthForm] = useState({
    mobile: '',
    operator: 'Airteldth'
  });
  
  const [planForm, setPlanForm] = useState({
    mobile: '',
    operator: 'Airtel',
    circle: 'Delhi NCR'
  });
  
  // Data states
  const [hlrResult, setHlrResult] = useState(null);
  const [dthResult, setDthResult] = useState(null);
  const [plans, setPlans] = useState([]);
  const [operators, setOperators] = useState([]);
  const [dthOperators, setDthOperators] = useState([]);
  const [circles, setCircles] = useState([]);

  useEffect(() => {
    if (show) {
      loadOperators();
      loadDthOperators();
      loadCircles();
    }
  }, [show]);

  const loadOperators = async () => {
    try {
      const response = await api.get('/api/hlr/operators');
      if (response.data.success) {
        setOperators(response.data.data);
      }
    } catch (err) {
      console.error('Error loading operators:', err);
    }
  };

  const loadDthOperators = async () => {
    try {
      const response = await api.get('/api/hlr/dth-operators');
      if (response.data.success) {
        setDthOperators(response.data.data);
      }
    } catch (err) {
      console.error('Error loading DTH operators:', err);
    }
  };

  const loadCircles = async () => {
    try {
      const response = await api.get('/api/hlr/circles');
      if (response.data.success) {
        setCircles(response.data.data);
      }
    } catch (err) {
      console.error('Error loading circles:', err);
    }
  };

  const handleHlrCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Backend + PaySprint expect { number, type }
    if (!hlrForm.mobile || hlrForm.mobile.trim().length < 10) {
      setError('Please enter a valid mobile number.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        number: hlrForm.mobile.trim(),
        type: 'mobile',
      };

      console.log('Sending HLR check request:', payload);
      // Increase timeout for HLR check – PaySprint can take longer than 10s
      const response = await api.post('/api/hlr/check', payload, { timeout: 30000 });
      
      if (response.data.success) {
        setHlrResult(response.data.data);
        setSuccess('HLR check completed successfully');
      } else {
        setError(response.data.message || 'HLR check failed');
      }
    } catch (err) {
      console.error('HLR check error', err);
      const errorData = err.response?.data;
      let errorMessage = 'HLR check failed';
      
      if (errorData) {
        // Handle validation errors
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          errorMessage = validationErrors.join(', ') || errorData.message || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDthInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate form data
    if (!dthForm.mobile || dthForm.mobile.trim().length < 10) {
      setError('Please enter a valid customer/subscriber ID.');
      setLoading(false);
      return;
    }
    if (!dthForm.operator || dthForm.operator.trim() === '') {
      setError('Please select a DTH operator.');
      setLoading(false);
      return;
    }

    // Map operator names to PaySprint operator codes
    const operatorMap = {
      'Airtel DTH': 'Airteldth',
      'Airteldth': 'Airteldth',
      'Tata Sky': 'TataSky',
      'TataSky': 'TataSky',
      'tatasky': 'TataSky',
      'Videocon D2H': 'Videocon',
      'Videocon': 'Videocon',
      'videocon': 'Videocon',
      'Sun Direct': 'Sundirect',
      'Sundirect': 'Sundirect',
      'sundirect': 'Sundirect',
      'Dish TV': 'Dishtv',
      'Dishtv': 'Dishtv',
      'dishtv': 'Dishtv'
    };

    // Get PaySprint operator code (default to TataSky if not found)
    const paySprintOp = operatorMap[dthForm.operator] || 'TataSky';

    try {
      // Backend + PaySprint expect { canumber, op }
      const payload = {
        canumber: String(dthForm.mobile).trim(),
        op: paySprintOp
      };

      console.log('Sending DTH info request:', payload);
      // Increase timeout for DTH info – external HLR/DTH API may be slow
      const response = await api.post('/api/hlr/dth-info', payload, { timeout: 30000 });
      
      if (response.data.success) {
        setDthResult(response.data.data);
        setSuccess('DTH info retrieved successfully');
      } else {
        setError(response.data.message || 'DTH info retrieval failed');
      }
    } catch (err) {
      console.error('DTH info error', err);
      const errorData = err.response?.data;
      let errorMessage = 'DTH info retrieval failed';
      
      if (errorData) {
        // Handle validation errors
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          errorMessage = validationErrors.join(', ') || errorData.message || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBrowsePlan = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate form data
    if (!planForm.operator || planForm.operator.trim() === '') {
      setError('Please select an operator.');
      setLoading(false);
      return;
    }
    if (!planForm.circle || planForm.circle.trim() === '') {
      setError('Please select a circle.');
      setLoading(false);
      return;
    }

    try {
      // Backend expects 'op' not 'operator', and doesn't need 'mobile'
      const payload = {
        op: String(planForm.operator).trim(),
        circle: String(planForm.circle).trim()
      };

      console.log('Sending browse-plan request:', payload);
      // Increase timeout for browse-plan – plan listing can be slower
      const response = await api.post('/api/hlr/browse-plan', payload, { timeout: 30000 });
      
      if (response.data.success) {
        setPlans(response.data.data?.plans || []);
        setSuccess('Plans retrieved successfully');
      } else {
        setError(response.data.message || 'Plan retrieval failed');
      }
    } catch (err) {
      console.error('Browse plan error', err);
      const errorData = err.response?.data;
      let errorMessage = 'Plan retrieval failed';
      
      if (errorData) {
        // Handle validation errors
        if (errorData.errors) {
          const validationErrors = Object.values(errorData.errors).flat();
          errorMessage = validationErrors.join(', ') || errorData.message || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatMobile = (mobile) => {
    return mobile.replace(/(\d{5})(\d{5})/, '$1 $2');
  };

  const getStatusIcon = (status) => {
    if (status === 'active' || status === 'success') {
      return <FaCheckCircle className="text-success" />;
    } else if (status === 'inactive' || status === 'failed') {
      return <FaTimesCircle className="text-danger" />;
    }
    return <FaInfoCircle className="text-info" />;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaMobile className="me-2" />
          HLR Services
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* HLR Check Tab */}
          <Tab eventKey="hlr" title="HLR Check">
            <Card>
              <Card.Header>
                <h5><FaSearch className="me-2" />Mobile Number Verification</h5>
                <small className="text-muted">Check mobile number details and operator information</small>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleHlrCheck}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number</Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={hlrForm.mobile}
                          onChange={(e) => setHlrForm({ ...hlrForm, mobile: e.target.value })}
                          maxLength="10"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Operator</Form.Label>
                        <Form.Select
                          value={hlrForm.operator}
                          onChange={(e) => setHlrForm({ ...hlrForm, operator: e.target.value })}
                        >
                          {operators.map(op => (
                            <option key={op.id} value={op.name}>{op.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaSearch className="me-2" />}
                    Check HLR
                  </Button>
                </Form>

                {hlrResult && (
                  <div className="mt-4">
                    <h6>HLR Check Result:</h6>
                    <div className="bg-light p-3 rounded">
                      <pre>{JSON.stringify(hlrResult, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* DTH Info Tab */}
          <Tab eventKey="dth" title="DTH Info">
            <Card>
              <Card.Header>
                <h5><FaTv className="me-2" />DTH Information</h5>
                <small className="text-muted">Get DTH subscriber information</small>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleDthInfo}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number</Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={dthForm.mobile}
                          onChange={(e) => setDthForm({ ...dthForm, mobile: e.target.value })}
                          maxLength="10"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>DTH Operator</Form.Label>
                        <Form.Select
                          value={dthForm.operator}
                          onChange={(e) => setDthForm({ ...dthForm, operator: e.target.value })}
                        >
                          {dthOperators.map(op => (
                            <option key={op.id} value={op.id}>{op.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button variant="info" type="submit" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaTv className="me-2" />}
                    Get DTH Info
                  </Button>
                </Form>

                {dthResult && (
                  <div className="mt-4">
                    <h6>DTH Information:</h6>
                    <div className="bg-light p-3 rounded">
                      <pre>{JSON.stringify(dthResult, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Browse Plan Tab */}
          <Tab eventKey="plans" title="Browse Plans">
            <Card>
              <Card.Header>
                <h5><FaInfoCircle className="me-2" />Browse Recharge Plans</h5>
                <small className="text-muted">View available recharge plans for mobile numbers</small>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleBrowsePlan}>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number</Form.Label>
                        <Form.Control
                          type="tel"
                          placeholder="Enter 10-digit mobile number"
                          value={planForm.mobile}
                          onChange={(e) => setPlanForm({ ...planForm, mobile: e.target.value })}
                          maxLength="10"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Operator</Form.Label>
                        <Form.Select
                          value={planForm.operator}
                          onChange={(e) => setPlanForm({ ...planForm, operator: e.target.value })}
                        >
                          {operators.map(op => (
                            <option key={op.id} value={op.name}>{op.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Circle</Form.Label>
                        <Form.Select
                          value={planForm.circle}
                          onChange={(e) => setPlanForm({ ...planForm, circle: e.target.value })}
                        >
                          {circles.map(circle => (
                            <option key={circle.id} value={circle.name}>{circle.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button variant="success" type="submit" disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : <FaInfoCircle className="me-2" />}
                    Browse Plans
                  </Button>
                </Form>

                {plans.length > 0 && (
                  <div className="mt-4">
                    <h6>Available Plans:</h6>
                    <div className="row">
                      {plans.map((plan, index) => (
                        <div key={index} className="col-md-6 mb-3">
                          <Card className="h-100">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="card-title">₹{plan.amount}</h6>
                                  <p className="card-text small">{plan.description}</p>
                                  <small className="text-muted">Validity: {plan.validity}</small>
                                </div>
                                <Button size="sm" variant="outline-primary">
                                  Select
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HLRModal;
